import logo from '@/assets/images/logo.svg';
/**
 * @spec docs/specs/plans/chip-migration-program.md
 * Privy SDK configuration: Base, plus Polygon mainnet for legacy USDT sends.
 */
import type { PrivyClientConfig } from '@privy-io/react-auth';
import { env } from './env';
import { getStakingChainId, CHAIN_CONFIG, LEGACY_POLYGON_CONFIG } from './chains';

// Privy App ID (must reuse from main platform for unified accounts)
export const PRIVY_APP_ID = env.privyAppId;

// Determine which chains to use based on environment
const stakingChainId = getStakingChainId();
const isTestnet = stakingChainId === CHAIN_CONFIG.BASE_SEPOLIA.id;

// Chain definitions for Privy (matching viem/chains format)
const baseMainnet = {
  id: CHAIN_CONFIG.BASE.id,
  name: CHAIN_CONFIG.BASE.name,
  network: 'base',
  nativeCurrency: CHAIN_CONFIG.BASE.nativeCurrency,
  rpcUrls: {
    default: { http: [CHAIN_CONFIG.BASE.rpcUrl] },
  },
  blockExplorers: {
    default: {
      name: CHAIN_CONFIG.BASE.explorerName,
      url: CHAIN_CONFIG.BASE.explorerUrl || 'https://basescan.org',
    },
  },
};

const baseSepoliaChain = {
  id: CHAIN_CONFIG.BASE_SEPOLIA.id,
  name: CHAIN_CONFIG.BASE_SEPOLIA.name,
  network: 'base-sepolia',
  nativeCurrency: CHAIN_CONFIG.BASE_SEPOLIA.nativeCurrency,
  rpcUrls: {
    default: { http: [CHAIN_CONFIG.BASE_SEPOLIA.rpcUrl] },
  },
  blockExplorers: {
    default: {
      name: CHAIN_CONFIG.BASE_SEPOLIA.explorerName,
      url: CHAIN_CONFIG.BASE_SEPOLIA.explorerUrl || 'https://sepolia.basescan.org',
    },
  },
};

// Privy's wallet.switchChain rejects any chain missing from this list ("Unsupported chainId",
// code 4901), so Polygon mainnet stays here for legacy USDT sends (useExternalSend); the token is
// mainnet USDT in every environment. Our own isSupportedChain still excludes Polygon.
//
// rpcUrls.default is not what the embedded wallet uses. Privy 1.99.1 routes embedded-wallet RPC
// through rpcUrls.privyWalletOverride, else config rpcUrls, else rpcUrls.privy (Privy's own RPC,
// which Privy adds to every supported chain it knows), and only then rpcUrls.public or
// rpcUrls.default. rpcUrls.default is what Privy offers an external wallet in
// wallet_addEthereumChain when wallet.switchChain meets a 4902; our own useNetworkSwitch adds
// chains with PUBLIC_RPC_URLS from chain-registry.
const polygonMainnet = {
  id: LEGACY_POLYGON_CONFIG.id,
  name: LEGACY_POLYGON_CONFIG.name,
  network: 'polygon',
  nativeCurrency: LEGACY_POLYGON_CONFIG.nativeCurrency,
  rpcUrls: {
    default: { http: [LEGACY_POLYGON_CONFIG.rpcUrl] },
  },
  blockExplorers: {
    default: {
      name: LEGACY_POLYGON_CONFIG.explorerName,
      url: LEGACY_POLYGON_CONFIG.explorerUrl || 'https://polygonscan.com',
    },
  },
};

const supportedChains = isTestnet
  ? [baseSepoliaChain, polygonMainnet]
  : [baseMainnet, polygonMainnet];

// Default chain is Base (mainnet or testnet depending on environment)
const defaultChain = isTestnet ? baseSepoliaChain : baseMainnet;

// Privy configuration - Phase 2: Full authentication support
export const privyConfig: PrivyClientConfig = {
  // Phase 2: Enable social auth methods alongside wallet connection
  loginMethods: ['email', 'wallet'],

  // Wallet appearance and supported wallets
  appearance: {
    theme: 'dark',
    accentColor: '#a046d3', // Brand purple
    logo: logo,
    walletList: [
      'metamask',
      'coinbase_wallet',
      'rainbow',
      'phantom',
      'detected_ethereum_wallets',
      'wallet_connect',
    ],
  },

  // Phase 2: Enable embedded wallet for users without external wallets
  embeddedWallets: {
    createOnLogin: 'users-without-wallets',
  },

  // Default chain: Base. It does not switch external wallets on connect. Privy uses it as the
  // embedded wallet's chain on initialisation when it has no stored chain for that address, and
  // as the one required chain of a WalletConnect session (shouldEnforceDefaultChainOnConnect is
  // set whenever defaultChain is); every other supported chain is optional in the session.
  defaultChain,
  supportedChains,

  // Default storage (localStorage) - works for same-origin deployment
  // For cross-subdomain, see Q4 in spec for cookie configuration
};

/**
 * Cross-subdomain configuration (for future reference)
 *
 * For cross-subdomain session sharing:
 * storage: {
 *   type: 'cookie',
 *   cookieOptions: {
 *     domain: '.phenompoker.com',
 *     secure: true,
 *     sameSite: 'lax',
 *   },
 * }
 */
